import {
    useCallback,
    useContext,
    useEffect,
    type FC,
    useRef
} from "react";
import {
    View
} from "react-native";
import type IDropdownMenuSubTriggerProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    DropdownMenuSubContext,
    useDropdownMenuContext
} from "../../context";
import {
    getTypeColors
} from "../../../../helpers/theme/typeColors";
import {
    ChevronRight as ChevronRightIcon
} from "lucide-react-native";
import {
    getMirrorStyle
} from "../../../../helpers/direction";
import {
    getIsDropdownMenuKeyboardFocus
} from "../../util";
import DropdownMenuRow from "../row";

const DropdownMenuSubTrigger: FC<IDropdownMenuSubTriggerProps> = ({
    customTheme: customThemeProp,
    isDisabled = false,
    valueText,
    ...props
}) => {
    const rootContext = useDropdownMenuContext({
        part: "SubTrigger"
    });

    const subContext = useContext(DropdownMenuSubContext);

    const customTheme = customThemeProp ?? rootContext?.customTheme;

    const {
        durations,
        iconSizes,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const rowRef = useRef<View | null>(null);

    const handleOpenChange = subContext?.handleOpenChange;

    const anchorRef = subContext?.anchorRef;

    const handleClearTimer = () => {
        if(timerRef.current === null) {
            return;
        }

        clearTimeout(timerRef.current);

        timerRef.current = null;
    };

    const handleOpenSub = useCallback(() => {
        handleOpenChange?.(true, "first");
    }, [
        handleOpenChange
    ]);

    useEffect(() => {
        return () => {
            if(timerRef.current !== null) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    if(!subContext || !anchorRef) {
        return null;
    }

    const color = isDisabled ? getTypeColors({
        variant: "ghost",
        type: "neutral",
        isDisabled,
        colors
    }).iconColor : colors.content.icon.mid;

    const onElementChange = (node: View | null) => {
        rowRef.current = node;

        anchorRef.current = node;
    };

    const handlePress = () => {
        handleOpenChange?.(true, getIsDropdownMenuKeyboardFocus(rowRef.current) ? "first" : "pointer");
    };

    const handleHoverIn = () => {
        handleClearTimer();

        if(isDisabled || subContext.isOpen) {
            return;
        }

        timerRef.current = setTimeout(() => {
            handleOpenChange?.(true, "pointer");
        }, durations.hoverIntent);
    };

    const renderChevron = () => {
        return <View
            style={[
                stylesheet.chevron,
                getMirrorStyle({
                    direction
                })
            ]}
            aria-hidden={true}
        >
            <ChevronRightIcon
                size={iconSizes.small}
                color={color}
            />
        </View>;
    };

    return <DropdownMenuRow
        {...props}
        onOpenSub={isDisabled ? undefined : handleOpenSub}
        onElementChange={onElementChange}
        ariaExpanded={subContext.isOpen}
        onHoverOut={handleClearTimer}
        isActive={subContext.isOpen}
        trailing={renderChevron()}
        id={subContext.triggerId}
        onHoverIn={handleHoverIn}
        customTheme={customTheme}
        subId={subContext.subId}
        isDisabled={isDisabled}
        isCloseOnSelect={false}
        onPress={handlePress}
        meta={valueText}
        role="menuitem"
    />;
};
export default DropdownMenuSubTrigger;
